'use client';

import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../atoms/dialog';
import type { ThemeScopeValue } from '../theme/constants';
import { cn } from '../utils';

type ModalProps = {
  children: React.ReactNode;
  title?: string;
  /** Screen-reader-only title when `title` is not displayed */
  ariaTitle?: string;
  className?: string;
  theme?: 'light' | 'dark';
  /**
   * Pins the portaled panel to one theme SCOPE — a different axis from `theme`
   * above, which only picks the card's chrome.
   *
   * The content lands on `document.body`, so it sits outside any `ThemeScope`
   * the caller is inside and resolves its roles against `<html>`: the app
   * theme, dark by default. A caller whose own surface is pinned (the pre-auth
   * login funnel is pinned light) passes its scope here, or the modal comes up
   * in the opposite ramp to the page that opened it.
   *
   * Leave unset to follow the app theme, which is what every visitor surface
   * wants.
   */
  contentTheme?: ThemeScopeValue;
  /**
   * Controls the underlying Radix `Dialog` open state. Defaults to `true` —
   * a plain modal that's shown for as long as it's rendered.
   *
   * Intercepted `@modal` routes must pass a pathname-derived value instead:
   * Next 16 keeps the intercept slot mounted (frozen via `<Activity>`) after
   * close, so a hardcoded `open` never toggles and Radix never unmounts the
   * content — leaving stale state (a half-entered OTP screen) on reopen. Driving
   * `open` off the live pathname makes it flip false→true across close→reopen,
   * so Radix unmounts + remounts the content subtree and it starts fresh.
   * Mirrors `TippingInterceptModal`.
   */
  open?: boolean;
  /**
   * Called when the user dismisses the dialog (✕ / backdrop / Esc). The host
   * owns what dismiss means — this pure UI molecule stays navigation-free (no
   * `useRouter`). The intercepted login modal passes `router.back()`.
   */
  onClose?: () => void;
  /**
   * Extra props forwarded to the underlying Radix `DialogContent` — e.g. the
   * reCAPTCHA outside-interaction guards so a phone-auth challenge overlay
   * stays clickable inside the modal.
   */
  contentProps?: Omit<
    React.ComponentProps<typeof DialogContent>,
    'children' | 'className'
  >;
  /**
   * Radix dialog modality. `true` (default) = a normal modal: scroll-lock +
   * focus-trap + backdrop. Set `false` ONLY for a dialog hosting a reCAPTCHA
   * image challenge.
   *
   * A modal Radix dialog wraps its content in `react-remove-scroll`, which sets
   * `pointer-events: none` on `<body>` and re-enables it only inside the dialog
   * content. Google portals the reCAPTCHA "select all …" challenge into a fixed
   * `<iframe>` appended to `<body>` — OUTSIDE the dialog content — so every
   * click on a challenge tile lands on the dimmed backdrop and the user can
   * never solve it. Non-modal drops that pointer shield (and the focus trap), so
   * the out-of-tree challenge is clickable. We still render a dim backdrop.
   * Leave `true` for ordinary modals so scroll-lock + focus-trap stay on.
   */
  modal?: boolean;
};

export function Modal({
  children,
  title,
  ariaTitle,
  className,
  theme,
  contentTheme,
  contentProps,
  open = true,
  onClose,
  modal = true,
}: ModalProps) {
  const handleOpenChange = (next: boolean) => {
    if (!next) {
      onClose?.();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange} modal={modal}>
      <DialogContent
        data-theme={contentTheme}
        // Forward the modal flag so a non-modal dialog renders the atom's
        // plain-div backdrop — Radix's `DialogOverlay` renders `null` when the
        // Root is non-modal, so without this a non-modal Modal has NO backdrop.
        modal={modal}
        className={cn(
          'font-sans',
          // The classes here are already theme ROLES; the trailing `dark`
          // scope class that used to follow them pinned every caller
          // (`ForceUpdateModal`, `ShareOrderDialog`) dark regardless of the
          // creator's toggle. `theme="dark"` now means "the standard themed
          // surface" rather than "force dark".
          theme === 'dark' && 'border-line bg-surface-1 text-content-1',
          theme === 'light' &&
            'border-content-1 theme-keep bg-white text-black',
          className,
        )}
        {...contentProps}
      >
        {title ? (
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
        ) : (
          <VisuallyHidden>
            <DialogTitle>{ariaTitle ?? 'Dialog'}</DialogTitle>
          </VisuallyHidden>
        )}
        {children}
      </DialogContent>
    </Dialog>
  );
}
